import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useLabourBoard, type LabourBoard } from "../../lib/api";
import { Money, SectionCard, Share, Stat } from "./shared";

/** Where the labour cost sits (HRX-03): by project, by activity, by person.
 *
 * Every figure here was zero until the rates were configured — 8,192 hours worth nothing. The unrated count is
 * kept on the strip rather than hidden, because a line with no rate is not a cheap line, it is an invisible one. */
export default function Cost({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useLabourBoard(company || undefined);

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;

  return (
    <div className="mt-4 space-y-4" data-labour-cost>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-labour-strip>
        <Stat label={t("lab.hours")} value={d.totals.hours.toLocaleString(lng === "fr" ? "fr-CD" : "en-GB")} hint={t("lab.linesN", { n: d.totals.lines })} />
        <Stat label={t("lab.cost")} value={<Money value={d.totals.cost} lng={lng} />} />
        <Stat label={t("lab.avgRate")} value={<Money value={d.totals.average_rate} lng={lng} />} hint={t("lab.perHour")} />
        <Stat label={t("lab.unapproved")} value={<Money value={d.approval.pending_cost} lng={lng} />}
          cls={d.approval.pending ? "text-status-amber" : ""} hint={t("lab.unapprovedHint", { n: d.approval.pending })} />
        <Stat label={t("lab.contractor")} value={<Money value={d.contractor.cost} lng={lng} />}
          hint={t("lab.contractorHint", { hours: d.contractor.hours })} />
      </div>

      <Billing d={d} lng={lng} />

      {d.totals.unrated_lines > 0 && (
        <div className="rounded border border-status-amber/40 bg-amber-50 px-4 py-2 text-sm text-status-amber" data-labour-unrated>
          {t("lab.unrated", { lines: d.totals.unrated_lines, hours: d.totals.unrated_hours })}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Rollup title={t("lab.byProject")} rows={d.by_project} lng={lng} link={(k) => `/projects/${k}`} />
        <Rollup title={t("lab.byActivity")} rows={d.by_activity} lng={lng} />
      </div>
      <Rollup title={t("lab.byPerson")} rows={d.by_person} lng={lng} />
    </div>
  );
}

function Billing({ d, lng }: { d: LabourBoard; lng: string }) {
  const { t } = useTranslation();
  const nothingSold = !d.billing.billable_amount;
  return (
    <SectionCard title={t("lab.billing")} aside={t("lab.customers", { n: d.billing.customers ?? 0 })}>
      <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-4" data-labour-billing>
        <Fact label={t("lab.billableHours")} value={d.billing.billable_hours.toFixed(0)} />
        <Fact label={t("lab.billableAmount")} value={<Money value={d.billing.billable_amount} lng={lng} />} />
        <Fact label={t("lab.writtenOff")} value={`${d.billing.written_off_hours.toFixed(0)} h`} />
        <Fact label={t("lab.margin")} value={d.billing.margin_pct === null ? "–" : `${d.billing.margin_pct} %`} />
      </div>
      {nothingSold && (
        <p className="border-t border-brand-grey-light bg-[#F6F8FA] px-4 py-2 text-[11px] text-brand-grey" data-labour-nothing-billable>
          {t("lab.nothingBillable")}
        </p>
      )}
    </SectionCard>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-brand-grey">{label}</div>
      <div className="font-heading text-lg font-semibold">{value}</div>
    </div>
  );
}

function Rollup({ title, rows, lng, link }: {
  title: string; rows: LabourBoard["by_project"]; lng: string; link?: (key: string) => string;
}) {
  const { t } = useTranslation();
  const top = rows[0]?.cost ?? 0;
  return (
    <SectionCard title={title} aside={t("lab.linesN", { n: rows.reduce((a, r) => a + r.lines, 0) })}>
      <table className="w-full text-sm">
        <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
          <tr>
            <th className="px-4 py-2">{title}</th>
            <th className="px-2 py-2 text-right">{t("lab.hours")}</th>
            <th className="px-2 py-2 text-right">{t("lab.rate")}</th>
            <th className="px-4 py-2 text-right">{t("lab.cost")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-brand-grey">{t("lab.none")}</td></tr>}
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-brand-grey-light" data-labour-row={r.key}>
              <td className="px-4 py-2">
                <div className="font-medium">
                  {link ? <Link to={link(r.key)} className="text-brand-primary hover:underline">{r.label}</Link> : r.label}
                </div>
                <div className="mt-1 max-w-[16rem]"><Share value={r.cost} of={top} /></div>
              </td>
              <td className="px-2 py-2 text-right tabular-nums">{r.hours.toLocaleString(lng === "fr" ? "fr-CD" : "en-GB")}</td>
              <td className="px-2 py-2 text-right text-brand-grey"><Money value={r.rate} lng={lng} /></td>
              <td className="px-4 py-2 text-right font-semibold"><Money value={r.cost} lng={lng} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionCard>
  );
}
